import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { ApiError, del, errorMessage, get, patch, post, put } from '../../api/client';
import type { FamilyTexts, LineupFamily, LineupModel, LineupState, NewsLang } from '../../api/types';
import { useMe } from '../../auth';
import { IconAlert, IconArrowDown, IconArrowUp, IconExternal, IconImage, IconPlus, IconTrash } from '../../components/icons';
import { DragHandle, type HandleProps, SortableList } from '../../components/sortable';
import { Field, Modal, PageHead, Spinner, Switch, useFeedback } from '../../components/ui';
import { useT } from '../../i18n';
import { fichaState, LangChips, LANGS, LINEUP_LIMITS } from './shared';

/** La gama entera (familias y motos): la usan la lista y el editor de cada moto. */
export const LINEUP_KEY = ['pm-lineup', 'list'] as const;
const ORDER = ['pm-lineup', 'order'] as const;

/** ¿La moto tiene ficha? Un lanzamiento sin foto sale solo como silueta. */
export const hasFicha = (m: Pick<LineupModel, 'launch' | 'image'>) => !m.launch || !!m.image;

/** Las motos en el orden del sitio: familia por familia. */
const byFamily = (families: LineupFamily[], models: LineupModel[]) => families.flatMap((f) => models.filter((m) => m.familyId === f.id));

/** La gama de la landing de Phelon & Moore: las familias (filas de la gama) con sus motos, en el orden en que se ven. */
export function PmLineup() {
  const { t, tn } = useT();
  const qc = useQueryClient();
  const { toast } = useFeedback();
  const { data, isPending, error } = useQuery({ queryKey: LINEUP_KEY, queryFn: () => get<LineupState>('/admin/pm/lineup') });
  const [dialog, setDialog] = useState<LineupFamily | 'new' | null>(null);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: LINEUP_KEY });
    void qc.invalidateQueries({ queryKey: ['pm-publish'] });
    void qc.invalidateQueries({ queryKey: ['overview'] });
  };

  // Los reordenamientos van en fila y solo el último vuelve a pedir la lista: arrastrar dos veces
  // seguido no la hace saltar a un orden intermedio.
  const reorder = useMutation({
    mutationKey: ORDER,
    scope: { id: 'pm-lineup-order' },
    mutationFn: (v: { kind: 'models'; familyId: string; ids: string[] } | { kind: 'families'; ids: string[] }) =>
      v.kind === 'models' ? put('/admin/pm/lineup/models/order', { familyId: v.familyId, ids: v.ids }) : put('/admin/pm/lineup/families/order', { ids: v.ids }),
    onSuccess: () => {
      if (qc.isMutating({ mutationKey: ORDER }) <= 1) void qc.invalidateQueries({ queryKey: LINEUP_KEY });
      void qc.invalidateQueries({ queryKey: ['pm-publish'] });
    },
    onError: (e) => {
      toast(errorMessage(e), 'error');
      refresh();
    },
  });

  const reorderModels = (familyId: string, next: LineupModel[]) => {
    qc.setQueryData<LineupState>(LINEUP_KEY, (old) =>
      old && { ...old, models: old.families.flatMap((f) => (f.id === familyId ? next : old.models.filter((m) => m.familyId === f.id))) },
    );
    reorder.mutate({ kind: 'models', familyId, ids: next.map((m) => m.id) });
  };

  const moveFamily = (index: number, delta: -1 | 1) => {
    const old = qc.getQueryData<LineupState>(LINEUP_KEY);
    if (!old) return;
    const families = [...old.families];
    const [f] = families.splice(index, 1);
    families.splice(index + delta, 0, f!);
    qc.setQueryData<LineupState>(LINEUP_KEY, { ...old, families, models: byFamily(families, old.models) });
    reorder.mutate({ kind: 'families', ids: families.map((x) => x.id) });
  };

  const setVisible = async (m: LineupModel, visible: boolean) => {
    try {
      const { model } = await patch<{ model: LineupModel }>(`/admin/pm/lineup/models/${m.id}`, { visible });
      qc.setQueryData(['pm-lineup', 'model', m.id], model);
      toast(visible ? t('Ahora es visible (se ve en el sitio al publicar)') : t('Quedó oculta (sale del sitio al publicar)'));
    } catch (e) {
      toast(errorMessage(e), 'error');
    }
    refresh();
  };

  const families = data?.families ?? [];
  const models = data?.models ?? [];

  return (
    <>
      <PageHead
        kicker={t('Landing')}
        title={t('Motos')}
        description={t(
          'La gama de la landing: cada familia es una fila (con su segmento encima) y una pestaña de la línea de producto; cada moto, su foto y su ficha. Arrastra para cambiar el orden. Los cambios se ven después de publicar.',
        )}
      >
        {data?.siteUrl && (
          <a className="btn" href={`${data.siteUrl}/#lineup`} target="_blank" rel="noreferrer">
            <IconExternal /> {t('Ver en el sitio')}
          </a>
        )}
        {data?.ready && (
          <button className="btn" onClick={() => setDialog('new')}>
            <IconPlus /> {t('Nueva familia')}
          </button>
        )}
        {data?.ready && families.length > 0 && (
          <Link to="/motos/nueva" className="btn btn-primary">
            <IconPlus /> {t('Nueva moto')}
          </Link>
        )}
      </PageHead>

      {isPending ? (
        <Spinner />
      ) : error || !data ? (
        <div className="notice error">{errorMessage(error)}</div>
      ) : !data.ready ? (
        <div className="notice warn">
          <IconAlert width={16} style={{ flex: 'none', marginTop: 2 }} />
          <span>{t('Falta aplicar la migración de la base para guardar motos (npm run db:deploy). Mientras tanto la landing sigue con la gama de su código.')}</span>
        </div>
      ) : families.length === 0 ? (
        <div className="panel empty-state">
          <strong>{t('Todavía no hay motos en el dashboard: la landing sigue con la gama de su código.')}</strong>
          <span>{t('Para traer las motos de hoy con sus fichas y sus fotos, corre la carga inicial (npm run db:seed:pm), o empieza creando la primera familia.')}</span>
          <button className="btn btn-primary" onClick={() => setDialog('new')}>
            <IconPlus /> {t('Nueva familia')}
          </button>
        </div>
      ) : (
        <div className="stack">
          {families.map((f, fi) => {
            const list = models.filter((m) => m.familyId === f.id);
            const visible = list.filter((m) => m.visible).length;
            return (
              <section key={f.id} className="panel lineup-family" style={{ overflow: 'hidden' }}>
                <header className="lineup-family-head">
                  <div style={{ minWidth: 0 }}>
                    <h2>
                      {f.name} <span className="faint lineup-segment">{f.texts.en.segment}</span>
                    </h2>
                    <div className="row row-wrap" style={{ gap: 8, marginTop: 4 }}>
                      <span className="faint" style={{ fontSize: 13 }}>
                        {list.length === visible ? tn(list.length, '{n} moto', '{n} motos') : t('{visible} de {total} visibles', { visible, total: list.length })}
                      </span>
                      <LangChips stateOf={(l) => (f.texts[l].segment ? 'full' : 'empty')} />
                    </div>
                  </div>
                  <div className="actions">
                    <button className="btn btn-sm btn-ghost btn-icon" disabled={fi === 0} onClick={() => moveFamily(fi, -1)} aria-label={t('Subir la familia')} title={t('Subir la familia')}>
                      <IconArrowUp />
                    </button>
                    <button
                      className="btn btn-sm btn-ghost btn-icon"
                      disabled={fi === families.length - 1}
                      onClick={() => moveFamily(fi, 1)}
                      aria-label={t('Bajar la familia')}
                      title={t('Bajar la familia')}
                    >
                      <IconArrowDown />
                    </button>
                    <button className="btn btn-sm" onClick={() => setDialog(f)}>
                      {t('Editar familia')}
                    </button>
                    <Link to={`/motos/nueva?familia=${f.id}`} className="btn btn-sm">
                      <IconPlus /> {t('Moto')}
                    </Link>
                  </div>
                </header>
                {list.length === 0 ? (
                  <div className="empty-state" style={{ padding: '18px 20px' }}>
                    {t('Esta familia todavía no tiene motos: no sale en el sitio hasta que tenga alguna visible.')}
                  </div>
                ) : (
                  <SortableList items={list} getId={(m) => m.id} onReorder={(next) => reorderModels(f.id, next)}>
                    {(m, _i, handle) => <ModelRow m={m} handle={handle} onVisible={(v) => void setVisible(m, v)} />}
                  </SortableList>
                )}
              </section>
            );
          })}
          <p className="faint" style={{ fontSize: 13, margin: 0 }}>
            {t(
              'En la gama, dos familias seguidas que suman 5 motos o menos comparten fila. Los textos «Fifteen Euro 5+ models…» y «15 models» se cuentan solos con las motos visibles de cada versión. Las ocultas no salen en el sitio.',
            )}
          </p>
        </div>
      )}

      {dialog && (
        <FamilyDialog
          family={dialog === 'new' ? null : dialog}
          count={dialog === 'new' ? 0 : models.filter((m) => m.familyId === dialog.id).length}
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null);
            refresh();
          }}
        />
      )}
    </>
  );
}

function ModelRow({ m, handle, onVisible }: { m: LineupModel; handle: HandleProps; onVisible: (v: boolean) => void }) {
  const { t, tn } = useT();
  const errors = m.issues.filter((x) => x.level === 'error').length;
  const warnings = m.issues.length - errors;
  const meta = [m.euro, hasFicha(m) ? m.texts.en.tag : t('Lanzamiento sin foto: sale como silueta, sin ficha')].filter(Boolean).join(' · ');
  return (
    <div className={`list-item lineup-row ${m.visible ? '' : 'is-hidden'}`}>
      <DragHandle {...handle} />
      <Link to={`/motos/${m.id}`} className="thumb thumb-cover lineup-thumb" aria-hidden="true" tabIndex={-1}>
        {m.image ? <img src={m.image.url} alt="" loading="lazy" /> : <IconImage />}
      </Link>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="row row-wrap" style={{ gap: 8 }}>
          <Link to={`/motos/${m.id}`} className="news-title">
            {m.name}
          </Link>
          {!m.visible && <span className="badge">{t('Oculta')}</span>}
          {m.start && <span className="badge info">{t('Ficha inicial')}</span>}
          {m.launch && <span className="badge">{t('Lanzamiento')}</span>}
          {m.limited && <span className="badge">{t('Edición limitada')}</span>}
          {m.versions.length === 1 && <span className="badge">{m.versions[0] === 'eu' ? t('Solo Europa') : t('Solo Global')}</span>}
          {errors > 0 && (
            <span className="badge red" title={m.issues.map((x) => x.message).join(' · ')}>
              <IconAlert width={12} /> {errors === 1 ? t('Falta algo') : t('Faltan {n} cosas', { n: errors })}
            </span>
          )}
        </div>
        {meta && <div className="faint news-meta">{meta}</div>}
        <div className="row row-wrap" style={{ gap: 8, marginTop: 6 }}>
          {hasFicha(m) && <LangChips stateOf={(l) => fichaState(m.texts, l)} />}
          {warnings > 0 && (
            <span
              className="faint"
              style={{ fontSize: 12 }}
              title={m.issues
                .filter((x) => x.level === 'warning')
                .map((x) => x.message)
                .join(' · ')}
            >
              <IconAlert width={12} style={{ verticalAlign: '-2px', color: 'var(--warn)' }} /> {tn(warnings, '{n} aviso', '{n} avisos')}
            </span>
          )}
        </div>
      </div>
      <div className="actions">
        <Switch checked={m.visible} onChange={onVisible} label={m.visible ? t('Visible') : t('Oculta')} />
        <Link to={`/motos/${m.id}`} className="btn btn-sm">
          {t('Editar')}
        </Link>
      </div>
    </div>
  );
}

const emptySegments = (): FamilyTexts => Object.fromEntries(LANGS.map((l) => [l.code, { segment: '' }])) as FamilyTexts;

/** Alta o cambios de una familia: su nombre y el segmento en los seis idiomas. Borrar: solo vacía y solo un administrador. */
function FamilyDialog({ family, count, onClose, onSaved }: { family: LineupFamily | null; count: number; onClose: () => void; onSaved: () => void }) {
  const { t, tr } = useT();
  const { data: me } = useMe();
  const { toast, confirm } = useFeedback();
  const [name, setName] = useState(family?.name ?? '');
  const [texts, setTexts] = useState<FamilyTexts>(family?.texts ?? emptySegments());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const setSegment = (l: NewsLang, segment: string) => setTexts((prev) => ({ ...prev, [l]: { segment } }));

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = { name: name.trim(), texts };
      if (family) await patch(`/admin/pm/lineup/families/${family.id}`, body);
      else await post('/admin/pm/lineup/families', body);
      toast(family ? t('Familia guardada. Se verá en el sitio después de publicar.') : t('Familia creada al final de la gama. Agrégale motos: sale en el sitio cuando tenga alguna visible.'));
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e : new ApiError(0, errorMessage(e)));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!family) return;
    const ok = await confirm(tr('¿Borrar la familia {name}?', { name: <strong>{t('«{name}»', { name: family.name })}</strong> }), { confirmLabel: t('Borrar'), danger: true });
    if (!ok) return;
    try {
      await del(`/admin/pm/lineup/families/${family.id}`);
      toast(t('Familia borrada'));
      onSaved();
    } catch (e) {
      toast(errorMessage(e), 'error');
    }
  };

  return (
    <Modal
      title={family ? t('Familia «{name}»', { name: family.name }) : t('Nueva familia')}
      onClose={onClose}
      wide
      footer={
        <>
          {family && me?.role === 'ADMIN' && (
            <button
              className="btn btn-danger"
              style={{ marginRight: 'auto' }}
              onClick={() => void remove()}
              disabled={count > 0 || busy}
              title={count > 0 ? t('Solo se puede borrar una familia sin motos: muévelas a otra o bórralas antes') : undefined}
            >
              <IconTrash /> {t('Borrar')}
            </button>
          )}
          <button className="btn" onClick={onClose}>
            {t('Cancelar')}
          </button>
          <button className="btn btn-primary" onClick={() => void save()} disabled={busy}>
            {busy ? t('Guardando…') : t('Guardar')}
          </button>
        </>
      }
    >
      <div className="stack">
        {error && !error.errors.length && <div className="notice error">{error.message}</div>}
        <Field
          label={t('Nombre')}
          error={error?.field('name')}
          count={{ value: name, max: LINEUP_LIMITS.familyName }}
          hint={t('Igual en los seis idiomas: va junto al segmento sobre la fila de la gama y en la pestaña de la línea de producto («Capetown»).')}
        >
          {(id) => <input id={id} className="input" value={name} placeholder="Capetown" onChange={(e) => setName(e.target.value)} />}
        </Field>
        <div className="stack" style={{ gap: 8 }}>
          <div>
            <h3 className="section-title" style={{ fontSize: 14 }}>
              {t('Segmento')}
            </h3>
            <p className="faint" style={{ margin: 0, fontSize: 12.5 }}>
              {t('Lo que se lee en mayúsculas sobre la fila («Adventure»). El inglés es obligatorio; lo que quede vacío sale en inglés.')}
            </p>
          </div>
          <div className="grid grid-2 family-segments">
            {LANGS.map((l) => (
              <Field
                key={l.code}
                label={l.code === 'en' ? t(l.name) : t('{label} (opcional)', { label: t(l.name) })}
                error={error?.field(`texts.${l.code}.segment`)}
                count={{ value: texts[l.code].segment, max: LINEUP_LIMITS.segment }}
              >
                {(id) => (
                  <input
                    id={id}
                    className="input"
                    lang={l.code}
                    value={texts[l.code].segment}
                    placeholder={l.code === 'en' ? 'Adventure' : texts.en.segment || undefined}
                    onChange={(e) => setSegment(l.code, e.target.value)}
                  />
                )}
              </Field>
            ))}
          </div>
        </div>
        {family && (
          <p className="faint" style={{ margin: 0, fontSize: 12.5 }}>
            {count > 0
              ? t('El orden de la familia en la gama se cambia con las flechas de la lista. Para borrarla, primero mueve sus motos a otra familia (en el editor de cada moto) o bórralas.')
              : t('El orden de la familia en la gama se cambia con las flechas de la lista.')}
          </p>
        )}
      </div>
    </Modal>
  );
}
